import Vue from 'vue'
import { clearExcelUploadStash, getExcelUploadStash, putExcelUploadStash } from './excel-upload-stash'
import { getBaseUrl } from './helpers'

const STORAGE_PREFIX = 'i18n-async-job:'
/**
 * Matches i18n-service Errors.ASYNC_JOB_NOT_FOUND in src/types/error-code.ts (UNKNOWN_ERROR = 1000
 * plus its position in the enum). Hand-copied: a code inserted rather than appended on the service
 * side silently shifts this. every call site also matches /找不到/ or a 404, so a drift degrades to a
 * missed fast-path rather than a wrong branch — but re-check this number when error codes change.
 */
const ASYNC_JOB_NOT_FOUND = 1026

function fingerprintPayload(data) {
  // Avoid JSON.stringify of tens of thousands of rows (blocks the UI on /#/fluid), but do walk
  // every row: sampling head/tail alone let two different uploads share a fingerprint, so the
  // second one resumed the first one's job.
  if (!data || typeof data !== 'object') {
    return String(data)
  }
  const rows = Array.isArray(data.data) ? data.data : []
  let rolling = 0
  for (const row of rows) {
    const text = `${row && row.key}\u0000${row && row.ticket}`
    for (let i = 0; i < text.length; i++) {
      rolling = (rolling * 31 + text.charCodeAt(i)) | 0
    }
  }
  return [
    rows.length,
    Array.isArray(data.selectedLang) ? data.selectedLang.join(',') : data.selectedLang,
    rolling,
  ].join('|')
}

export function createIdempotencyKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

function storageKey(operation) {
  return `${STORAGE_PREFIX}${operation}`
}

export function jobScope(operation, method, url) {
  return `${operation}:${String(method).toUpperCase()}:${url}`
}

export function rememberJob(operation, value) {
  try {
    const pending = typeof value === 'string' ? { jobId: value } : value
    sessionStorage.setItem(storageKey(operation), JSON.stringify(pending))
  } catch (_) {
    // ignore quota / private mode
  }
}

export function forgetJob(operation) {
  try {
    sessionStorage.removeItem(storageKey(operation))
  } catch (_) {
    // ignore
  }
}

export function rememberedJob(operation) {
  try {
    const value = sessionStorage.getItem(storageKey(operation))
    if (!value) return null
    try {
      const parsed = JSON.parse(value)
      return parsed && typeof parsed === 'object' ? parsed : null
    } catch (_) {
      // Backward compatibility for active jobs saved by the first async release.
      return { jobId: value }
    }
  } catch (_) {
    return null
  }
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

async function pollJob(jobId) {
  let delay = 1000
  for (let i = 0; i < 600; i++) {
    const res = await Vue.axios.get(`${getBaseUrl()}/jobs/${jobId}`)
    if (res.data.code !== 0) {
      const error = new Error(res.data.msg || '任務狀態查詢失敗')
      error.jobNotFound =
        res.data.code === ASYNC_JOB_NOT_FOUND ||
        /找不到/.test(res.data.msg || '') ||
        (res.status === 404)
      throw error
    }
    const job = res.data.data || {}
    if (job.status === 'succeeded') {
      return fetchJobResult(jobId)
    }
    if (job.status === 'failed') {
      const message = (job.error && job.error.message) || '非同步任務失敗'
      const error = new Error(message)
      error.jobError = job.error
      error.jobTerminal = true
      throw error
    }
    await sleep(delay)
    delay = Math.min(5000, Math.floor(delay * 1.2))
  }
  throw new Error('非同步任務等待逾時')
}

function mergePagedResult(acc, page) {
  if (!page || typeof page !== 'object') {
    return page
  }
  if (!acc) {
    return page
  }
  const merged = { ...acc, ...page }
  if (Array.isArray(acc.errors) || Array.isArray(page.errors)) {
    merged.errors = [...(acc.errors || []), ...(page.errors || [])]
  }
  if (Array.isArray(acc.data) || Array.isArray(page.data)) {
    merged.data = [...(acc.data || []), ...(page.data || [])]
  }
  return merged
}

async function fetchJobResult(jobId) {
  let cursor = -1
  let acc = null
  for (let i = 0; i < 100; i++) {
    const resultRes = await Vue.axios.get(`${getBaseUrl()}/jobs/${jobId}/result`, {
      params: { cursor, limit: 20 },
    })
    if (resultRes.data.code !== 0) {
      throw new Error(resultRes.data.msg || '任務結果查詢失敗')
    }
    const data = resultRes.data.data || {}
    acc = mergePagedResult(acc, data.result)
    if (data.done) {
      return acc
    }
    const nextCursor = data.cursor
    if (typeof nextCursor !== 'number' || nextCursor <= cursor) {
      throw new Error('任務結果分頁游標無效')
    }
    cursor = nextCursor
  }
  throw new Error('任務結果分頁讀取逾時')
}

async function lookupJobIdOnce(idempotencyKey, operationId) {
  try {
    const res = await Vue.axios.get(`${getBaseUrl()}/jobs`, {
      params: { idempotencyKey, operationId },
    })
    if (res.data && res.data.code === 0 && res.data.data && res.data.data.jobId) {
      return res.data.data.jobId
    }
    const code = res.data && res.data.code
    if (code === ASYNC_JOB_NOT_FOUND || /找不到/.test((res.data && res.data.msg) || '')) {
      return null
    }
  } catch (error) {
    const status = error && error.response && error.response.status
    const code = error && error.response && error.response.data && error.response.data.code
    if (
      status === 404 ||
      code === ASYNC_JOB_NOT_FOUND ||
      (error && error.jobNotFound)
    ) {
      return null
    }
    throw error
  }
  return null
}

async function lookupJobId(idempotencyKey, operationId, { attempts = 1, delayMs = 0 } = {}) {
  for (let i = 0; i < attempts; i++) {
    const jobId = await lookupJobIdOnce(idempotencyKey, operationId)
    if (jobId) {
      return jobId
    }
    if (i < attempts - 1) {
      await sleep(delayMs)
    }
  }
  return null
}

async function finishExcelJob(scope, project) {
  forgetJob(scope)
  await clearExcelUploadStash(project)
}

function readRememberedJob(scope, operation) {
  let existing = rememberedJob(scope)
  if (!existing) {
    const legacy = rememberedJob(operation)
    if (legacy) {
      existing = legacy
      rememberJob(scope, legacy)
      forgetJob(operation)
    }
  }
  return existing
}

export async function resumeRememberedJob({ operation, method, url, operationId, project }) {
  const scope = jobScope(operation, method, url)
  let existing = readRememberedJob(scope, operation)
  const stash = project ? await getExcelUploadStash(project) : null
  if (!existing && stash && stash.idempotencyKey) {
    existing = { idempotencyKey: stash.idempotencyKey }
    rememberJob(scope, existing)
  }
  if (!existing) {
    return null
  }

  let jobId = existing.jobId
  if (!jobId && existing.idempotencyKey) {
    jobId = await lookupJobId(existing.idempotencyKey, operationId || operation, {
      attempts: 5,
      delayMs: 2000,
    })
    if (jobId) {
      rememberJob(scope, { ...existing, jobId })
    }
  }
  if (!jobId) {
    if (stash && stash.file) {
      return {
        needsReplay: true,
        idempotencyKey: existing.idempotencyKey || stash.idempotencyKey,
        selectedLang: stash.selectedLang || '',
        file: stash.file,
      }
    }
    return null
  }

  try {
    const result = await pollJob(jobId)
    await finishExcelJob(scope, project)
    return { async: true, result }
  } catch (error) {
    if (error.jobNotFound || error.jobTerminal) {
      await finishExcelJob(scope, project)
    }
    if (error.jobNotFound) {
      if (stash && stash.file) {
        return {
          needsReplay: true,
          idempotencyKey: existing.idempotencyKey || stash.idempotencyKey,
          selectedLang: stash.selectedLang || '',
          file: stash.file,
        }
      }
      return null
    }
    throw error
  }
}

export async function submitAndAwaitJob({
  method,
  url,
  data,
  operation,
  file,
  project,
  replay = false,
}) {
  const scope = jobScope(operation, method, url)
  const fingerprint = fingerprintPayload(data)
  let existing = readRememberedJob(scope, operation)
  if (existing && existing.fingerprint && existing.fingerprint !== fingerprint && !replay) {
    await finishExcelJob(scope, project)
    existing = null
  }
  if (existing && existing.jobId) {
    try {
      const result = await pollJob(existing.jobId)
      await finishExcelJob(scope, project)
      return { async: true, result }
    } catch (error) {
      if (error.jobNotFound || error.jobTerminal) {
        await finishExcelJob(scope, project)
      }
      if (!error.jobNotFound) {
        throw error
      }
      existing = null
    }
  }

  const idempotencyKey =
    (existing && existing.idempotencyKey) || createIdempotencyKey()
  if (file && project) {
    await putExcelUploadStash({
      project,
      idempotencyKey,
      selectedLang: data && data.selectedLang,
      fileName: file.name,
      file,
    })
  }
  rememberJob(scope, { idempotencyKey, fingerprint })
  // Body fallback: some proxies/CDNs drop custom headers on large POSTs.
  const requestData =
    data && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, idempotencyKey }
      : { payload: data, idempotencyKey }
  let res
  const maxSubmitAttempts = 4
  for (let attempt = 0; attempt < maxSubmitAttempts; attempt++) {
    try {
      res = await Vue.axios({
        method,
        url,
        data: requestData,
        headers: { 'Idempotency-Key': idempotencyKey },
      })
      break
    } catch (error) {
      // Keep the key for network/5xx failures because the server may have accepted
      // the request (CloudFront/nginx 503 while enqueue is still running). Retry the
      // same Idempotency-Key so a finished staging job is reused as 202.
      const status = error && error.response && error.response.status
      if (status >= 400 && status < 500) {
        await finishExcelJob(scope, project)
        throw error
      }
      if (attempt === maxSubmitAttempts - 1) {
        throw error
      }
      await sleep(1500 * (attempt + 1))
    }
  }

  const jobId = res.data && res.data.data && res.data.data.jobId
  // Prefer HTTP 202; also accept a jobId envelope if a proxy rewrote the status.
  if (jobId && (res.status === 202 || res.data.data.status)) {
    rememberJob(scope, { jobId, idempotencyKey, fingerprint })
    try {
      const result = await pollJob(jobId)
      await finishExcelJob(scope, project)
      return { async: true, result, envelope: res.data }
    } catch (error) {
      if (error.jobNotFound || error.jobTerminal) {
        await finishExcelJob(scope, project)
      }
      throw error
    }
  }

  await finishExcelJob(scope, project)
  return { async: false, result: res.data && res.data.data, envelope: res.data }
}
